import { createContext, useContext, useReducer } from 'react';
import { VirtualCardIssuanceResponse } from '@ads-bread/shared/bread/codecs';
import { useMounted } from '../lib/hooks/useMounted';
import { FCWithChildren } from '../lib/types';

export type Action =
  | { type: 'reset'; state: AppData }
  | { type: 'setVirtualCardIssuance'; issuance: VirtualCardIssuanceResponse };

export type AppData = {
  issuance?: VirtualCardIssuanceResponse;
};

export type ProviderValueType = {
  data: AppData;
  dispatch: React.Dispatch<Action>;
  reset: () => void;
};

export const getInitialState = (): AppData => {
  return {};
};

// For test mocks
export const initialState = getInitialState();

const AppDataContext = createContext<ProviderValueType>({
  data: getInitialState(),
  dispatch: () => {
    // no op
  },
  reset: () => {
    // no op
  },
});

function reducer(state: AppData, action: Action): AppData {
  switch (action.type) {
    case 'reset':
      return action.state;
    case 'setVirtualCardIssuance':
      return {
        ...state,
        issuance: action.issuance,
      };
  }
}

interface ProviderProps {
  state?: AppData;
}

export const AppDataContextProvider: FCWithChildren<ProviderProps> = ({
  children,
  state = getInitialState(),
}) => {
  const [data, dispatch] = useReducer(reducer, state);

  const reset = () => {
    dispatch({ type: 'reset', state });
  };

  const isMounted = useMounted();
  const wrappedDispatch = (action: Action) => {
    if (isMounted.current) {
      dispatch(action);
    }
  };

  return (
    <AppDataContext.Provider value={{ data, dispatch: wrappedDispatch, reset }}>
      {children}
    </AppDataContext.Provider>
  );
};
export type AppDataAPI = {
  data: AppData;
  reset: () => void;
  setVirtualCardIssuance: (issuance: VirtualCardIssuanceResponse) => void;
};

export const useAppData = (): AppDataAPI => {
  const { data, dispatch, reset } = useContext(AppDataContext);

  return {
    data,
    reset,
    setVirtualCardIssuance: (issuance: VirtualCardIssuanceResponse) =>
      dispatch({ type: 'setVirtualCardIssuance', issuance }),
  };
};
